import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { blogPosts } from '../utils/mockData';
import ReactMarkdown from 'react-markdown';

const BlogPost = () => {
  const { id } = useParams();
  const post = blogPosts.find(p => p.id === id);

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#1e3a8a] mb-4">Artículo no encontrado</h1>
          <Link to="/blog" className="text-[#84cc16] hover:underline">
            Volver al Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Image */}
      <div className="relative h-96 w-full">
        <img 
          src={post.imagen}
          alt={post.titulo}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 -mt-32 relative z-10">
        {/* Back button */}
        <Link 
          to="/blog" 
          className="inline-flex items-center gap-2 text-white hover:text-gray-200 mb-6 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          Volver al Blog
        </Link>

        {/* Article Card */}
        <Card className="shadow-2xl">
          <CardContent className="p-8 md:p-12">
            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-500">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {post.fecha}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                5 min lectura
              </span>
              <span className="px-3 py-1 bg-[#84cc16] text-white rounded-full text-xs font-semibold">
                {post.categoria}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1e3a8a] mb-6">
              {post.titulo}
            </h1>

            {/* Author */}
            <div className="flex items-center gap-3 mb-8 pb-8 border-b">
              <div className="h-14 w-14 bg-[#1e40af] rounded-full flex items-center justify-center text-white font-bold text-lg">
                {post.autor.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <p className="font-semibold text-gray-800 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {post.autor}
                </p>
                <p className="text-sm text-gray-500">Autor</p>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({node, ...props}) => <h1 className="text-4xl font-bold text-[#1e3a8a] mt-8 mb-4" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-3xl font-bold text-[#1e3a8a] mt-8 mb-4" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-2xl font-semibold text-[#1e3a8a] mt-6 mb-3" {...props} />,
                  p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 mb-4 text-gray-700" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-2 mb-4 text-gray-700" {...props} />,
                  li: ({node, ...props}) => <li className="ml-4" {...props} />,
                  blockquote: ({node, ...props}) => (
                    <blockquote className="border-l-4 border-[#84cc16] pl-4 italic text-gray-600 my-4 bg-gray-50 p-4 rounded-r" {...props} />
                  ),
                  strong: ({node, ...props}) => <strong className="font-bold text-[#1e3a8a]" {...props} />,
                  a: ({node, ...props}) => <a className="text-[#84cc16] hover:underline" {...props} />,
                  code: ({node, ...props}) => <code className="bg-gray-100 px-2 py-1 rounded text-sm" {...props} />,
                }}
              >
                {post.contenido}
              </ReactMarkdown>
            </div>

            {/* Divider */}
            <div className="border-t my-12"></div>

            {/* Call to action */}
            <div className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-4">¿Necesitas más información?</h3>
              <p className="mb-6">Contáctanos o agenda un chequeo para tu mascota</p>
              <Link to="/agendar">
                <button className="bg-[#84cc16] hover:bg-[#65a30d] text-white px-8 py-3 rounded-full font-semibold transition-colors">
                  Agendar cita
                </button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Related articles */}
        <div className="mt-12 mb-12">
          <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6">Artículos relacionados</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {blogPosts
              .filter(p => p.id !== id)
              .slice(0, 2)
              .map((relatedPost) => (
                <Link to={`/blog/${relatedPost.id}`} key={relatedPost.id}>
                  <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden h-full">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={relatedPost.imagen}
                        alt={relatedPost.titulo}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <CardContent className="p-6">
                      <h4 className="text-xl font-bold text-[#1e3a8a] mb-2 group-hover:text-[#84cc16] transition-colors">
                        {relatedPost.titulo}
                      </h4>
                      <p className="text-gray-600 text-sm line-clamp-2">{relatedPost.resumen}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
